<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import ConversationReportsAPI from 'dashboard/api/conversationReports';

const store = useStore();
const { t } = useI18n();

const agents = useMapGetter('agents/getAgents');
const globalConfig = useMapGetter('globalConfig/get');

const conversations = ref([]);
const meta = ref({
  total_count: 0,
  current_page: 1,
  total_pages: 1,
  avg_wait_seconds: null,
  avg_service_seconds: null,
});
const isLoading = ref(false);
const isExporting = ref(false);
const isGeneratingPdf = ref(false);

const filters = ref({ since: null, until: null, userId: null });
const currentPage = ref(1);

const agentOptions = computed(() => [
  { id: null, name: t('CONVERSATION_REPORT.FILTERS.ALL_AGENTS') },
  ...agents.value,
]);

const fetchConversations = async (page = 1) => {
  isLoading.value = true;
  try {
    const { data } = await ConversationReportsAPI.get({
      page,
      since: filters.value.since,
      until: filters.value.until,
      assigneeId: filters.value.userId,
    });
    conversations.value = data.data;
    meta.value = data.meta;
    currentPage.value = page;
  } catch {
    useAlert(t('CONVERSATION_REPORT.ERRORS.LOAD_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const onDateRangeChange = ([start, end]) => {
  filters.value.since = start ? Math.floor(new Date(start).getTime() / 1000) : null;
  filters.value.until = end ? Math.floor(new Date(end).getTime() / 1000) : null;
  fetchConversations(1);
};

const onFilterChange = () => fetchConversations(1);

const exportCSV = async () => {
  isExporting.value = true;
  try {
    const { data } = await ConversationReportsAPI.download({
      since: filters.value.since,
      until: filters.value.until,
      assigneeId: filters.value.userId,
    });
    downloadCsvFile('relatorio_conversas.csv', data);
  } catch {
    useAlert(t('CONVERSATION_REPORT.ERRORS.DOWNLOAD_FAILED'));
  } finally {
    isExporting.value = false;
  }
};

const formatDateTime = iso => {
  if (!iso) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'America/Sao_Paulo',
  }).format(new Date(iso));
};

const fmtDuration = secs => {
  if (secs == null) return '-';
  const s = Math.round(secs);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
};

const CLOSE_SCRIPT = '\x3C/script>';

const fmtTs = ts =>
  ts
    ? new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'America/Sao_Paulo',
      }).format(new Date(ts * 1000))
    : null;

const exportPDF = async () => {
  isGeneratingPdf.value = true;
  try {
    const logoPath = globalConfig.value?.logoThumbnail;
    let logoHtml = `<span style="font-size:20px;font-weight:700;color:#fff;letter-spacing:-0.5px">${globalConfig.value?.name || 'TalkChat'}</span>`;
    if (logoPath) {
      try {
        const res = await fetch(`${window.location.origin}${logoPath}`);
        const blob = await res.blob();
        const b64 = await new Promise(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        logoHtml = `<img src="${b64}" style="height:40px;max-width:160px;object-fit:contain;filter:brightness(0) invert(1)" alt="logo">`;
      } catch { /* fallback to text */ }
    }

    const companyName = globalConfig.value?.name || 'TalkChat';
    const since = fmtTs(filters.value.since);
    const until = fmtTs(filters.value.until);
    const periodLabel = since && until
      ? `${since} até ${until}`
      : since ? `A partir de ${since}` : 'Todos os períodos';

    const selectedAgent = filters.value.userId
      ? agents.value.find(a => a.id === filters.value.userId)?.name || 'Desconhecido'
      : 'Todos os atendentes';

    const now = new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo',
    }).format(new Date());

    const avgWait = fmtDuration(meta.value.avg_wait_seconds);
    const avgService = fmtDuration(meta.value.avg_service_seconds);

    const rows = conversations.value.map(conv => `<tr>
      <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9;color:#475569;font-variant-numeric:tabular-nums">${formatDateTime(conv.created_at)}</td>
      <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9;color:#1e293b;font-weight:500">#${conv.id}</td>
      <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9;color:#475569">${conv.protocol || '-'}</td>
      <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9;color:#475569">${conv.assignee_name || '-'}</td>
      <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9;color:#475569;font-variant-numeric:tabular-nums">${conv.contact_phone || '-'}</td>
      <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9;color:#475569">${conv.contact_name || '-'}</td>
      <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9;color:#475569">${(conv.labels || []).join(', ') || '-'}</td>
      <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9;color:#475569;font-variant-numeric:tabular-nums">${formatDateTime(conv.created_at)}</td>
      <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9;color:#475569;font-variant-numeric:tabular-nums">${formatDateTime(conv.last_activity_at)}</td>
      <td style="padding:9px 14px;border-bottom:1px solid #f1f5f9;color:#475569;font-variant-numeric:tabular-nums">${formatDateTime(conv.resolved_at)}</td>
    </tr>`).join('');

    const emptyRow = `<tr><td colspan="10" style="text-align:center;padding:40px;color:#94a3b8;font-size:13px">Nenhum registro encontrado para os filtros selecionados.</td></tr>`;

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<title>Relatório Analítico de Conversas</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,sans-serif;font-size:11px;color:#1e293b;background:#fff}
.header{background:#00B5A9;padding:20px 28px;display:flex;align-items:center;gap:16px}
.header-text h1{color:#fff;font-size:17px;font-weight:700}
.header-text p{color:rgba(255,255,255,.75);font-size:11px;margin-top:3px}
.meta{padding:14px 28px;background:#f8fafc;border-bottom:1px solid #e2e8f0;display:flex;gap:28px;flex-wrap:wrap}
.meta-item{font-size:11px;color:#64748b}
.meta-item strong{color:#1e293b;display:block;font-size:12px;margin-bottom:1px}
.kpi-bar{padding:12px 28px;display:flex;gap:24px;background:#f0fdfa;border-bottom:1px solid #ccfbf1}
.kpi-card{background:#fff;border:1px solid #99f6e4;border-radius:8px;padding:10px 16px;min-width:180px}
.kpi-label{font-size:10px;color:#14b8a6;font-weight:700;text-transform:uppercase;letter-spacing:.05em}
.kpi-value{font-size:18px;font-weight:700;color:#0f766e;margin-top:2px}
.content{padding:20px 28px}
table{width:100%;border-collapse:collapse}
thead tr{background:#f8fafc;border-top:2px solid #00B5A9}
th{padding:8px 10px;text-align:left;font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#64748b}
tbody tr:last-child td{border-bottom:none}
.footer{margin:0 28px 20px;padding-top:12px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:10px;color:#94a3b8}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style>
</head>
<body>
<div class="header">
  ${logoHtml}
  <div class="header-text">
    <h1>Relatório Analítico de Conversas</h1>
    <p>${companyName}</p>
  </div>
</div>
<div class="meta">
  <div class="meta-item"><strong>Período</strong>${periodLabel}</div>
  <div class="meta-item"><strong>Atendente</strong>${selectedAgent}</div>
  <div class="meta-item"><strong>Registros</strong>${conversations.value.length} (página atual)</div>
</div>
<div class="kpi-bar">
  <div class="kpi-card">
    <div class="kpi-label">Tempo Médio de Espera</div>
    <div class="kpi-value">${avgWait}</div>
  </div>
  <div class="kpi-card">
    <div class="kpi-label">Tempo Médio de Atendimento</div>
    <div class="kpi-value">${avgService}</div>
  </div>
</div>
<div class="content">
  <table>
    <thead>
      <tr>
        <th>Data/Hora Abertura</th><th>ID</th><th>Protocolo</th><th>Atendente</th><th>Número</th><th>Cliente</th><th>Etiquetas</th><th>1ª Mensagem</th><th>Última Mensagem</th><th>Fechamento</th>
      </tr>
    </thead>
    <tbody>${rows || emptyRow}</tbody>
  </table>
</div>
<div class="footer">
  <span>Gerado em ${now}</span>
  <span>${companyName} · TalkChat</span>
</div>
<script>window.onload=function(){window.print()}${CLOSE_SCRIPT}
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    window.open(URL.createObjectURL(blob), '_blank');
  } finally {
    isGeneratingPdf.value = false;
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  fetchConversations();
});
</script>

<template>
  <div class="flex flex-col gap-6 print:gap-4">
    <div class="flex items-center justify-between print:hidden">
      <h2 class="text-xl font-semibold text-n-slate-12">
        {{ $t('CONVERSATION_REPORT.TITLE') }}
      </h2>
      <div class="flex gap-2">
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors disabled:opacity-50"
          :disabled="isLoading"
          @click="fetchConversations(currentPage)"
        >
          <span
            :class="isLoading ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-refresh-cw'"
            class="size-4"
          />
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors disabled:opacity-50"
          :disabled="isExporting"
          @click="exportCSV"
        >
          <span class="i-lucide-file-spreadsheet size-4" />
          {{ isExporting ? $t('CONVERSATION_REPORT.EXPORTING') : $t('CONVERSATION_REPORT.EXPORT_CSV') }}
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors disabled:opacity-50"
          :disabled="isGeneratingPdf"
          @click="exportPDF"
        >
          <span
            :class="isGeneratingPdf ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-file-text'"
            class="size-4"
          />
          {{ isGeneratingPdf ? $t('CONVERSATION_REPORT.EXPORTING') : $t('CONVERSATION_REPORT.EXPORT_PDF') }}
        </button>
      </div>
    </div>

    <div class="flex flex-wrap gap-3 print:hidden">
      <WootDatePicker @date-range-changed="onDateRangeChange" />

      <select
        v-model="filters.userId"
        class="px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-teal-8"
        @change="onFilterChange"
      >
        <option
          v-for="agent in agentOptions"
          :key="String(agent.id)"
          :value="agent.id"
        >
          {{ agent.name }}
        </option>
      </select>
    </div>

    <div class="grid grid-cols-2 gap-3">
      <div class="rounded-xl border border-n-weak bg-n-surface-1 px-5 py-4">
        <p class="text-xs font-semibold uppercase tracking-wide text-n-teal-9">
          {{ $t('CONVERSATION_REPORT.STATS.AVG_WAIT') }}
        </p>
        <p class="mt-1 text-2xl font-bold text-n-slate-12">
          {{ fmtDuration(meta.avg_wait_seconds) }}
        </p>
      </div>
      <div class="rounded-xl border border-n-weak bg-n-surface-1 px-5 py-4">
        <p class="text-xs font-semibold uppercase tracking-wide text-n-teal-9">
          {{ $t('CONVERSATION_REPORT.STATS.AVG_SERVICE') }}
        </p>
        <p class="mt-1 text-2xl font-bold text-n-slate-12">
          {{ fmtDuration(meta.avg_service_seconds) }}
        </p>
      </div>
    </div>

    <div class="overflow-x-auto rounded-xl border border-n-weak">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-n-weak bg-n-surface-2">
            <th class="px-4 py-3 text-left font-medium text-n-slate-11 whitespace-nowrap">
              {{ $t('CONVERSATION_REPORT.TABLE.DATE') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11">
              {{ $t('CONVERSATION_REPORT.TABLE.ID') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11 whitespace-nowrap">
              {{ $t('CONVERSATION_REPORT.TABLE.PROTOCOL') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11 whitespace-nowrap">
              {{ $t('CONVERSATION_REPORT.TABLE.AGENT') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11 whitespace-nowrap">
              {{ $t('CONVERSATION_REPORT.TABLE.PHONE') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11 whitespace-nowrap">
              {{ $t('CONVERSATION_REPORT.TABLE.CONTACT') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11 whitespace-nowrap">
              {{ $t('CONVERSATION_REPORT.TABLE.LABELS') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11 whitespace-nowrap">
              {{ $t('CONVERSATION_REPORT.TABLE.FIRST_MESSAGE_AT') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11 whitespace-nowrap">
              {{ $t('CONVERSATION_REPORT.TABLE.LAST_MESSAGE_AT') }}
            </th>
            <th class="px-4 py-3 text-left font-medium text-n-slate-11 whitespace-nowrap">
              {{ $t('CONVERSATION_REPORT.TABLE.RESOLVED_AT') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td colspan="10" class="px-4 py-8 text-center text-n-slate-11">
              <span class="i-lucide-loader-2 animate-spin inline-block size-5" />
            </td>
          </tr>
          <tr v-else-if="conversations.length === 0">
            <td colspan="10" class="px-4 py-8 text-center text-n-slate-11">
              {{ $t('CONVERSATION_REPORT.NO_DATA') }}
            </td>
          </tr>
          <tr
            v-for="conv in conversations"
            :key="conv.id"
            class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1"
          >
            <td class="px-4 py-3 text-n-slate-11 tabular-nums whitespace-nowrap">{{ formatDateTime(conv.created_at) }}</td>
            <td class="px-4 py-3 font-medium text-n-slate-12">#{{ conv.id }}</td>
            <td class="px-4 py-3 text-n-slate-11">{{ conv.protocol || '-' }}</td>
            <td class="px-4 py-3 text-n-slate-11">{{ conv.assignee_name || '-' }}</td>
            <td class="px-4 py-3 text-n-slate-11 tabular-nums">{{ conv.contact_phone || '-' }}</td>
            <td class="px-4 py-3 text-n-slate-11">{{ conv.contact_name || '-' }}</td>
            <td class="px-4 py-3 text-n-slate-11">{{ (conv.labels || []).join(', ') || '-' }}</td>
            <td class="px-4 py-3 text-n-slate-11 tabular-nums whitespace-nowrap">{{ formatDateTime(conv.created_at) }}</td>
            <td class="px-4 py-3 text-n-slate-11 tabular-nums whitespace-nowrap">{{ formatDateTime(conv.last_activity_at) }}</td>
            <td class="px-4 py-3 text-n-slate-11 tabular-nums whitespace-nowrap">{{ formatDateTime(conv.resolved_at) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="meta.total_pages > 1"
      class="flex items-center justify-between print:hidden"
    >
      <span class="text-sm text-n-slate-11">
        {{ $t('CONVERSATION_REPORT.PAGINATION', { total: meta.total_count }) }}
      </span>
      <div class="flex gap-1">
        <button
          v-for="page in meta.total_pages"
          :key="page"
          class="px-3 py-1 text-sm rounded-lg border border-n-weak transition-colors"
          :class="page === currentPage ? 'bg-n-teal-9 text-white border-n-teal-9' : 'text-n-slate-11 hover:bg-n-alpha-1'"
          @click="fetchConversations(page)"
        >
          {{ page }}
        </button>
      </div>
    </div>
  </div>
</template>
